import React, { useCallback, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { Button } from 'ui-components/components/button';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import style from './style.module.less';
import Global from '../../../store/global-selectors';
import { emptyArray } from 'ui-components/default-props/empty';
import { cancelBatchRequest } from '../../../store/batches/batches.actions';
import { validateLabelBatchCancel, cancelBatch } from '../../../store/batches/batches.api';

const messages = defineMessages({
  cancelThisBatch: {
    description: 'batchCancel.cancelThisBatch',
    defaultMessage: 'Cancel this batch',
    id: 'cgRTIM',
  },
  cancelInstructions: {
    description: 'batchCancel.cancelInstructions',
    defaultMessage: 'Cancelling this batch will free the shipments to be added to other batches. This batch number will not be reused.',
    id: 'U+QkNp',
  },
  pickBatchInProgressWarning: {
    description: 'batchCancel.pickBatchInProgressWarning',
    defaultMessage: 'This batch has pick batches in progress. Cancelling will also cancel the pick batches.',
    id: 'VGcD6a',
  },
  close: {
    description: 'batchCancel.close',
    defaultMessage: "No, Don't Cancel Batch",
    id: 'zlHfIG',
  },
  cancelButton: {
    description: 'batchCancel.cancelButton',
    defaultMessage: 'Yes, Cancel Batch',
    id: 'nqzz+F',
  },
});

const renderCancelBatch = (cancel, isValidating, validationResult, pickBatchesInProgress) => {
  // Only show warning if validation is complete and there are pick batches
  const showPickBatchWarning = !isValidating && validationResult && pickBatchesInProgress && pickBatchesInProgress.length > 0;

  return (
    <div>
      <div className={style['cancel-header']}>
        <FormattedMessage {...messages.cancelThisBatch} />
      </div>
      <div className={style['cancel-batch-instructions']}>
        <FormattedMessage {...messages.cancelInstructions} />

        {isValidating && (
          <div className={style['validation-loading']}>
            <LoadingIndicator />
          </div>
        )}

        {showPickBatchWarning && (
          <div className={style['pick-batch-warning']}>
            <FormattedMessage {...messages.pickBatchInProgressWarning} />
          </div>
        )}
      </div>
      <div className={style['cancel-batch-buttons-wrapper']}>
        <Button className={classnames(style['close-cancel-dropdown'], style['cancel-buttons'])} disabled={isValidating} theme='tertiary'>
          <div className={style['cancel-batch-button-text']}>
            <FormattedMessage {...messages.close} />
          </div>
        </Button>
        <Button
          className={classnames(style['cancel-batch-dropdown'], style['cancel-buttons'])}
          disabled={isValidating}
          onClick={() => cancel()}
          theme='danger'
        >
          <div className={classnames(style['confirm-cancel-batch-button-text'], style['cancel-batch-button-text'])}>
            <FormattedMessage {...messages.cancelButton} />
          </div>
        </Button>
      </div>
    </div>
  );
};

const shouldAllowDropdownToClose = (e) => {
  if (!e.target.classList || !e.target.classList.length) {
    return false;
  }
  return [...new Set(e.target.classList)].some((cssClass) => {
    return cssClass.indexOf('cancel-buttons') !== -1;
  });
};

export const BatchCancelButton = ({ isDetails = false, disabled = false }) => {
  const dispatch = useDispatch();
  const batch = useSelector(Global.selectBatchDetailsBatch);
  const selectedBatches = useSelector(Global.selectBatchesSelectedBatches) || emptyArray;

  // Track validation state
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [pickBatchesInProgress, setPickBatchesInProgress] = useState([]);

  const handleDropdownOpen = useCallback(() => {
    // Reset state and start validation when dropdown opens
    setIsValidating(true);
    setValidationResult(null);
    setPickBatchesInProgress([]);

    const batchIds = isDetails ? [batch.fulfillmentPlanSetId] : selectedBatches.map((selectedBatch) => selectedBatch.fulfillmentPlanSetId);

    validateLabelBatchCancel(batchIds).subscribe({
      next: (response) => {
        setValidationResult(response);
        setPickBatchesInProgress(response.pickBatchesInProgress || []);
        setIsValidating(false);
      },
      error: () => {
        setValidationResult(null);
        setPickBatchesInProgress([]);
        setIsValidating(false);
      },
    });
  }, [batch, isDetails, selectedBatches]);

  const cancel = useCallback(() => {
    const batchIds = isDetails ? [batch.fulfillmentPlanSetId] : selectedBatches.map((selectedBatch) => selectedBatch.fulfillmentPlanSetId);

    // Call the API directly since we're force deleting by default now
    cancelBatch(batchIds).subscribe({
      next: () => {
        // Reset state after successful cancellation
        setValidationResult(null);
        setPickBatchesInProgress([]);
        // Dispatch success action if needed by other parts of the app
        dispatch(cancelBatchRequest(batchIds, isDetails));
      },
      error: () => {
        // Error handling is done by the epic
      },
    });
  }, [batch, dispatch, isDetails, selectedBatches]);

  return (
    <div className={style['cancel-batch-wrapper']}>
      <DropdownMenu
        className={style['cancel-batch-button']}
        closeWhenChildrenClicked={false}
        containerAllowedMeToClose={shouldAllowDropdownToClose}
        disabled={disabled}
        menuPosition='bottom-right'
        onOpen={handleDropdownOpen}
      >
        <DropdownMenuToggler className={style['cancel-batch-span']} hideChevron theme='tertiary'>
          <FormattedMessage defaultMessage='Cancel Batch' description='batchCancel.cancelBatch' id='76OeT+' />
        </DropdownMenuToggler>
        {renderCancelBatch(cancel, isValidating, validationResult, pickBatchesInProgress)}
      </DropdownMenu>
    </div>
  );
};

BatchCancelButton.propTypes = {
  isDetails: PropTypes.bool,
  disabled: PropTypes.bool,
};
